<template>
  <div class="hr-recruitment-show">
    <page-header title="RECRUITMENT">
      <template #action>
        <v-btn
          :to="{ name: 'hr-project-show', params: { id: projectId } }"
          link
          color="button"
          class="fw-400"
        >
          BACK
        </v-btn>
      </template>
      <template #breadcrumbs>
        <router-link :to="{ name: 'hr-projects' }">
          Projects
        </router-link>
        <span>/</span>
        <router-link :to="{ name: 'hr-project-show', params: { id: projectId } }">
          {{ recruitmentData.project_name }}
        </router-link>
        <span>/ Recruitment - {{ recruitmentData.position }}</span>
      </template>
    </page-header>

    <div class="recruitment-show__content">
      <RecruitmentDetailsForm
        v-if="isSuperHR"
        :recruitment-base="recruitmentData"
        :submit-name="submitName"
        @submit="update"
      />

      <RecruitmentDetails
        v-else
        :recruitment-data="recruitmentData"
        @edit-status="showRecruitmentStatusModal"
      />

      <v-row>
        <v-col cols="12" lg="6" class="pt-0">
          <ProjectNotesCard :notes="this.recruitmentData.notes || ''"
            @edit="openNotesModal"
          />
        </v-col>

        <v-col cols="12" lg="6" class="pt-0">
          <AttachmentsCard
            :attachments="recruitmentData.attachments"
            @edit="openFileModal"
            @delete="deleteAttachment"
          />
        </v-col>
      </v-row>

      <SectionHeader
        title="CANDIDATES"
        :subtitle="`${acceptedCandidates.length} CANDIDATES`"
      />

      <div class="hr-recruitment-show__accepted-candidates">
        <v-row v-if="acceptedCandidates.length">
          <v-col
            v-for="candidate in acceptedCandidates"
            :key="candidate.id"
            class="col-12 col-lg-6"
          >
            <candidate-card :candidate="candidate" dev-route="hr-developer-show">
              <template #menu>
                <v-list dense elevation-1>
                  <v-list-item
                    link
                    :to="{
                        name: 'hr-candidate-show',
                        params: {
                          projectId: projectId,
                          recruitmentId: recruitmentId,
                          developerId: candidate.developer_id,
                        }
                      }"
                  >
                    <v-list-item-title>Profile</v-list-item-title>
                  </v-list-item>
                  <v-list-item
                    v-if="candidate.status === 'Accepted'"
                    @click="showEmploymentModal(candidate)"
                  >
                    <v-list-item-title class="hr-recruitment-show__employment">
                      Employment
                    </v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="showCandidateStatusModal(candidate)">
                    <v-list-item-title>Status</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="deleteCandidateFromList(candidate)">
                    <v-list-item-title>Delete</v-list-item-title>
                  </v-list-item>
                </v-list>
              </template>
            </candidate-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col class="my-5">
            <NoData
              title="The recruitment list is empty"
              description="The project has no proposed developers"
            />
          </v-col>
        </v-row>
      </div>

      <section-header
        title="DRAFT CANDIDATES"
        :subtitle="`${draftCandidates.length} CANDIDATES`"
      >
        <template #action>
          <v-btn
            v-if="selectedDrafts.length"
            color="blue"
            class="hr-recruitment-show__submit-btn"
            @click="addToRecruitment"
          >
            Send to client ({{ selectedDrafts.length }})
          </v-btn>
          <v-btn
            link
            color="gray"
            class="hr-recruitment-show__search-btn"
            :to="{ name: 'hr-candidates',
              params: { projectId: projectId, recruitmentId: recruitmentId }}"
          >
            SEARCH CANDIDATE
          </v-btn>
        </template>
      </section-header>

      <div class="hr-recruitment-show__draft-candidates">
        <v-row v-if="draftCandidates.length">
          <v-col
            v-for="candidate in draftCandidates"
            :key="candidate.id"
            class="col-12 col-lg-6"
          >
            <candidate-draft-card :candidate="candidate" @change="selectDrafts">
              <template #menu>
                <v-list dense elevation-1>
                  <v-list-item
                    link
                    :to="{
                        name: 'hr-candidate-show',
                        params: {
                          projectId: projectId,
                          recruitmentId: recruitmentId,
                          developerId: candidate.developer_id,
                        }
                      }"
                  >
                    <v-list-item-title>Profile</v-list-item-title>
                  </v-list-item>
                  <v-list-item @click="deleteCandidateFromList(candidate)">
                    <v-list-item-title>Delete from draft</v-list-item-title>
                  </v-list-item>
                </v-list>
              </template>
            </candidate-draft-card>
          </v-col>
        </v-row>

        <v-row v-else>
          <v-col class="my-5">
            <NoData
              title="The list of proposed developers is empty"
              description="To add dev to the list, search for them by clicking the button below"
            />
          </v-col>
        </v-row>
      </div>
    </div>

    <CandidateStatusModal
      v-model="isCandidateStatusModalVisible"
      :candidate="selectedCadidate"
      :processing="isSubmitProcessing"
      @submit="changeCandidateStatus"
    />

    <ContractFormModal
      v-model="isEmploymentModalVisible"
      :candidate="selectedCadidate"
      :processing="isSubmitProcessing"
      submit-name="Create"
      @submit="submitContract"
    />

    <RecruitmentStatusModal
      v-model="isRecruitmentStatusModalVisible"
      :recruitment="recruitmentData"
      :processing="isSubmitProcessing"
      @submit="changeRecruitmentStatus"
    />

    <EditTextModal
      v-model="isNotesModalVisible"
      :description="this.recruitmentData.notes || ''"
      @add="addNotes"
    />

    <AttachmentsModal
      v-model="isFileModalVisible"
      :attachments="recruitmentData.attachments"
      :isSubmitProcessing="isAttachmentProcessing"
      @addAttachments="addAttachments"
    />

    <ConfirmDialog ref="confirmDialog" />
  </div>
</template>

<script>
import { mapState, mapActions, mapGetters } from 'vuex';
import PageHeader from '@/components/PageHeader.vue';
import NoData from '@/components/NoData.vue';
import SectionHeader from '@/components/SectionHeader.vue';
import RecruitmentDetails from '@/components/HrRecruitments/RecruitmentDetails.vue';
import ContractFormModal from '@/components/HrRecruitments/ContractFormModal.vue';
import CandidateStatusModal from '@/components/HrRecruitments/CandidateStatusModal.vue';
import CandidateCard from '@/components/CandidateCard.vue';
import CandidateDraftCard from '@/components/CandidateDraftCard.vue';
import ConfirmDialog from '@/components/ConfirmDialog.vue';
import RecruitmentStatusModal from '@/components/HrRecruitments/RecruitmentStatusModal.vue';
import ProjectNotesCard from '@/components/ProjectNotesCard.vue';
import EditTextModal from '@/components/EditTextModal.vue';
import AttachmentsCard from '@/components/AttachmentsCard.vue';
import AttachmentsModal from '@/components/AttachmentsModal.vue';
import RecruitmentDetailsForm from '@/components/Recruitments/RecruitmentDetailsForm.vue';


export default {
  metaInfo() {
    return {
      title: `Recruitment - ${this.recruitmentData.position}`,
    };
  },

  components: {
    PageHeader,
    NoData,
    SectionHeader,
    RecruitmentDetails,
    CandidateStatusModal,
    ContractFormModal,
    CandidateCard,
    CandidateDraftCard,
    ConfirmDialog,
    RecruitmentStatusModal,
    ProjectNotesCard,
    AttachmentsCard,
    EditTextModal,
    AttachmentsModal,
    RecruitmentDetailsForm,
  },

  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
    recruitmentId: {
      type: [String, Number],
      required: true,
    },
  },

  data() {
    return {
      isEmploymentModalVisible: false,
      isCandidateStatusModalVisible: false,
      isSubmitProcessing: false,
      selectedDrafts: [],
      selectedCadidate: {},
      isRecruitmentStatusModalVisible: false,
      isNotesModalVisible: false,
      isFileModalVisible: false,
      submitName: 'Save Recruitment',
    };
  },

  computed: {
    ...mapState('hrRecruitment', [
      'recruitmentData',
      'isAttachmentSubmitProcessing',
    ]),

    ...mapState('attachment', ['isAttachmentProcessing']),

    ...mapGetters('auth', [
      'isSuperHR',
    ]),

    acceptedCandidates() {
      return this.recruitmentData.candidates.filter((candidate) => candidate.status !== 'Draft');
    },

    draftCandidates() {
      return this.recruitmentData.candidates.filter((candidate) => candidate.status === 'Draft');
    },
  },

  created() {
    this.fetchRecruitment(this.recruitmentId);
  },

  methods: {
    ...mapActions('hrRecruitment', [
      'fetchRecruitment',
      'updateRecruitmentStatus',
      'updateNotes',
      'updateRecruitment',
      'addProjectNotes',
    ]),

    ...mapActions('hrCandidates', [
      'bulkOffer',
      'deleteCandidate',
      'updateCandidateStatus',
      'createContract',
    ]),

    ...mapActions('attachment', [
      'uploadAttachments',
      'deleteAttachments',
    ]),

    showEmploymentModal(candidate) {
      this.selectedCadidate = candidate;
      this.isEmploymentModalVisible = true;
    },

    deleteCandidateFromList(candidate) {
      this.$refs.confirmDialog
        .open({
          message: 'Are you sure you want to delete?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            const candidateData = {
              developer_id: candidate.developer_id,
              recruitment_id: this.recruitmentId,
            };
            this.deleteCandidate(candidateData)
              .then(() => {
                this.selectDrafts(candidate);
                this.fetchRecruitment(this.recruitmentId);
              });
          }
        });
    },

    selectDrafts(candidate) {
      if (candidate.selected) {
        this.selectedDrafts.push(candidate.id);
      } else {
        const index = this.selectedDrafts.indexOf(candidate.id);
        this.selectedDrafts.splice(index, 1);
      }
    },

    addToRecruitment() {
      this.$refs.confirmDialog
        .open({
          message: 'Are you sure you want to add candidates to recruitment list?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            this.bulkOffer(this.selectedDrafts)
              .then(() => {
                this.selectedDrafts = [];
                this.fetchRecruitment(this.recruitmentId);
              });
          }
        });
    },

    submitContract(contract) {
      this.isSubmitProcessing = true;
      const contractData = {
        projectId: this.projectId,
        data: contract,
      };
      this.createContract(contractData)
        .then(() => {
          this.isEmploymentModalVisible = false;
          this.selectedCadidate = {};
          this.fetchRecruitment(this.recruitmentId);
        })
        .finally(() => {
          this.isSubmitProcessing = false;
        });
    },

    showCandidateStatusModal(candidate) {
      this.selectedCadidate = candidate;
      this.isCandidateStatusModalVisible = true;
    },

    changeCandidateStatus(candidateParams) {
      this.isSubmitProcessing = true;

      this.updateCandidateStatus(candidateParams)
        .then(() => {
          this.isCandidateStatusModalVisible = false;
          this.selectedCadidate = {};
          this.isSubmitProcessing = false;
          this.fetchRecruitment(this.recruitmentId);
        });
    },

    addNotes(data) {
      const notesParams = {
        id: this.recruitmentId,
        notes: data,
      };
      if (this.isSuperHR) {
        this.addProjectNotes(data);
        this.updateRecruitment(this.recruitmentData);
      } else {
        this.updateNotes(notesParams);
      }
    },

    addAttachments(files) {
      const attachmentData = {
        attachments: files,
        apiType: 'hr',
        id: this.recruitmentId,
        recordType: 'recruitment',
      };

      this.uploadAttachments(attachmentData)
        .then(() => {
          this.isFileModalVisible = false;
          this.fetchRecruitment(this.recruitmentId);
        });
    },

    deleteAttachment(attachmentId) {
      const attachmentData = {
        id: attachmentId,
        apiType: 'hr',
      };

      this.$refs.confirmDialog
        .open({
          message: 'Are you sure you want to delete?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
        })
        .then((confirm) => {
          if (confirm) {
            this.deleteAttachments(attachmentData)
              .then(() => {
                this.fetchRecruitment(this.recruitmentId);
              });
          }
        });
    },

    showRecruitmentStatusModal() {
      this.isRecruitmentStatusModalVisible = true;
    },

    openNotesModal() {
      this.isNotesModalVisible = true;
    },

    openFileModal() {
      this.isFileModalVisible = true;
    },

    changeRecruitmentStatus(recruitmentParams) {
      this.isSubmitProcessing = true;

      this.updateRecruitmentStatus(recruitmentParams)
        .then(() => {
          this.isRecruitmentStatusModalVisible = false;
          this.selectedCadidate = {};
          this.fetchRecruitment(this.recruitmentId);
        })
        .finally(() => {
          this.isSubmitProcessing = false;
        });
    },

    update(recruitment) {
      this.updateRecruitment(recruitment);
    },
  },
};
</script>

<style lang="scss" scoped>
.hr-recruitment-show {
  &__search-btn {
    font-weight: 400;
  }

  &__accepted-candidates {
    margin-bottom: 16px;
  }

  &__draft-candidates {
    margin-bottom: 16px;
  }

  &__submit-btn {
    color: white;
    font-weight: 400;
    margin-right: 16px;
  }

  &__employment {
    color: $color--blue;
  }
}
</style>
